import { useEffect, type FormEvent, type ReactNode, type MouseEvent } from 'react';

export type ModalSize = 'sm' | 'md' | 'lg';

interface ModalProps {
  open: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
  actions?: ReactNode;
  size?: ModalSize;
  closeOnBackdrop?: boolean;
  closeDisabled?: boolean;
  className?: string;
  asForm?: boolean;
  onSubmit?: (event: FormEvent) => void;
}

export function Modal({
  open,
  title,
  subtitle,
  onClose,
  children,
  actions,
  size = 'md',
  closeOnBackdrop = true,
  closeDisabled = false,
  className = '',
  asForm = false,
  onSubmit
}: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !closeDisabled) onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, closeDisabled, onClose]);

  if (!open) return null;

  const handleBackdropClick = () => {
    if (closeOnBackdrop && !closeDisabled) onClose();
  };

  const stop = (event: MouseEvent) => event.stopPropagation();
  const panelClass = `modal-panel modal-${size}${className ? ` ${className}` : ''}`;
  const titleId = `modal-title-${title.replace(/\s+/g, '-').toLowerCase()}`;

  const body = (
    <>
      <div className="modal-header">
        <div>
          <h3 id={titleId}>{title}</h3>
          {subtitle ? <p className="modal-subtitle">{subtitle}</p> : null}
        </div>
        <button
          type="button"
          className="ghost-btn compact"
          disabled={closeDisabled}
          onClick={onClose}
        >
          Close
        </button>
      </div>
      <div className="modal-body">{children}</div>
      {actions ? <div className="modal-actions">{actions}</div> : null}
    </>
  );

  return (
    <div className="modal-backdrop" role="presentation" onClick={handleBackdropClick}>
      {asForm ? (
        <form
          className={panelClass}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          onClick={stop}
          onSubmit={onSubmit}
        >
          {body}
        </form>
      ) : (
        <div
          className={panelClass}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          onClick={stop}
        >
          {body}
        </div>
      )}
    </div>
  );
}
